import { RootState } from '@/store/store'
import { createSelector, createSelectorCreator, lruMemoize } from '@reduxjs/toolkit'
import { ConversationHeader } from './schema'
import { AgentConversation } from '@/agent/conversations/store/slice'
import { kNewConversationTitle } from './constants'
import { mapToChatListSidebarItem } from './util'
import { getConversationTimestamp } from '../conversation-navigation/util/get-conversation-timestamp'

/**
 * Selector: All Conversations
 * Returns all conversations as an array
 */
export const selectAllConversations = createSelector(
  (state: RootState) => state.agentConversationsState.conversations,
  (conversations) => Object.values(conversations).filter((conv): conv is AgentConversation => conv !== undefined)
)

/**
 * Conversation Headers Equality
 * Purpose: Avoids re-rendering every time a conversation changes in the Record.
 *          We only want to re-render when header values change.
 *
 * What triggers a re-render
 * - If the length of the previous and next lists are different (conversation added or removed)
 * - If the id does not match (e.g. if one was removed and another was inserted)
 * - If the modified_on value of any conversation changes
 * - If the title of any conversation changes
 */
const conversationHeaderListEquality = (prevList: ConversationHeader[], nextList: ConversationHeader[]): boolean => {
  // If the lengths are different, they are not equal
  if (prevList.length !== nextList.length) return false

  // Compare each conversation
  for (let i = 0; i < prevList.length; i++) {
    const prev = prevList[i]
    const next = nextList[i]

    // If the ids are different, they are not equal
    if (prev.id !== next.id) return false

    // If the modified_on values are different, they are not equal
    if (prev.modified_on !== next.modified_on) return false

    // If the titles are different, they are not equal
    if (prev.title !== next.title) return false
  }

  return true
}

/**
 * Selector Creator
 * A custom selector creator that uses the custom equality operator
 */
const createConversationListDataSelector = createSelectorCreator(lruMemoize, conversationHeaderListEquality)

/**
 * Selector: Conversations By Matter
 * Returns a sorted list of conversations for the given matter, most recent first.
 *
 * Excludes other members' empty "New Chat" placeholders: these are view-only noise
 * that no one but the owner should see or land on. Your own "New Chat" and everyone's
 * used chats are kept. When `currentUserId` is unknown, ownership can't be determined
 * so nothing is filtered on that basis.
 *
 * Returns empty array if no matterId is provided.
 */
export const selectConversationsByMatter = createSelector(
  selectAllConversations,
  (_: RootState, props: { matterId: string | null; currentUserId: string | undefined }) => props.matterId,
  (_: RootState, props: { matterId: string | null; currentUserId: string | undefined }) => props.currentUserId,
  (allConversations, matterId, currentUserId) => {
    // No matter selected = no conversations to show
    if (!matterId) return []

    if (!allConversations || !allConversations.length) return []

    // Filter to the matter, dropping other members' empty "New Chat" placeholders
    const conversationsByMatter = allConversations.filter(
      (conversation) =>
        conversation.matter_id === matterId && (!currentUserId || conversation.user_id === currentUserId || conversation.title !== kNewConversationTitle)
    )

    // Sort by the modified_on / updated_at conversation timestamp, most recent first
    return conversationsByMatter.sort((a, b) => getConversationTimestamp(b) - getConversationTimestamp(a))
  }
)

/**
 * Selector: Has Conversations For Matter
 * Returns true if there is at least one conversation for the given matter, applying the same
 * filtering as `selectConversationsByMatter` (other members' empty "New Chat" placeholders don't count).
 */
export const selectHasConversationsForMatter = createSelector(selectConversationsByMatter, (conversations) => conversations.length > 0)

/**
 * Selector: Recent Conversations By Matter
 * Returns a sorted list of all conversations for the given matter (across all features),
 * most recent first. Excludes "New Chat" placeholders.
 * Returns empty array if no matterId is provided.
 */
export const selectRecentConversationsByMatter = createSelector(
  selectAllConversations,
  (_: RootState, props: { matterId: string | null }) => props.matterId,
  (allConversations, matterId) => {
    // No matter selected = no conversations to show
    if (!matterId) return []

    if (!allConversations?.length) return []

    const forMatter = allConversations.filter((c) => c.matter_id === matterId && c.title !== kNewConversationTitle)
    return forMatter.sort((a, b) => getConversationTimestamp(b) - getConversationTimestamp(a))
  }
)

/**
 * Selector: Chat List Sidebar Items
 *
 * Returns a memoized list of `ChatListSidebarItem`s for a given matter.
 * This is used specifically for rendering the Chat List Sidebar UI.
 *
 * Internally:
 * - Builds on `selectConversationsByMatter`, which filters conversations by matter id
 *   and drops other members' empty "New Chat" placeholders
 * - Maps each conversation to a `ChatListSidebarItem` for display
 *
 * Memoization:
 * - Uses a custom equality check to avoid re-renders unless relevant values change,
 *   ensuring the sidebar only updates when necessary
 */

export const selectChatListSidebarItems = createConversationListDataSelector(selectConversationsByMatter, (conversations) => {
  if (!conversations || conversations.length === 0) {
    return []
  }
  return mapToChatListSidebarItem(conversations)
})
